import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, CalendarBlank, ChartLineUp, ClipboardText, Hammer, ListChecks, Plus, Rocket,
} from "@phosphor-icons/react";
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtPeriod } from "@/lib/period";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, StateBadge } from "@/components/shared";
import { Ring, ScoreBar } from "@/components/frameworkBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TABS = [
  { key: "workshops", label: "Workshop status", icon: Hammer },
  { key: "assessment", label: "Start assessment", icon: Rocket },
  { key: "monthly", label: "Month wise summary", icon: CalendarBlank },
  { key: "overall", label: "Overall summary", icon: ChartLineUp },
  { key: "commitments", label: "Commitments", icon: ListChecks },
  { key: "reviews", label: "Weekly review", icon: ClipboardText },
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"].map((m) => `${m} 2026`);
const monthLabel = (m) => fmtPeriod(m);

const BANDS = [
  [8, "Institutionalised", "text-success"],
  [6, "Systematised", "text-success"],
  [4, "Developing", "text-[hsl(28_82%_38%)]"],
  [2, "Informal / fragmented", "text-destructive"],
  [0, "Founder dependent", "text-destructive"],
];
const band = (v) => (v == null ? "Not scored" : BANDS.find(([t]) => v >= t)[1]);
const bandTone = (v) => (v == null ? "text-muted-foreground" : BANDS.find(([t]) => v >= t)[2]);

const WorkbookCell = ({ workshop, base, framework }) => {
  const { tenantId } = useAuth();
  const [files, setFiles] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const scopeKey = `${framework}:${workshop.element_code}:${workshop.id}`;

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/tenants/${tenantId}/attachments`, {
        params: { scope_type: "workbook", scope_key: scopeKey, framework },
      });
      setFiles(res.data);
    } catch { setFiles([]); }
  }, [tenantId, scopeKey, framework]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    const form = new FormData();
    form.append("scope_type", "workbook");
    form.append("scope_key", scopeKey);
    form.append("framework", framework);
    form.append("file", file);
    try {
      await api.post(`/tenants/${tenantId}/attachments`, form);
      toast.success(`Workbook saved as version ${files.length + 1}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const latest = files[0];

  return (
    <div className="text-xs" data-testid={`ew-workbook-${workshop.id}`}>
      {latest ? (
        <div>
          <a href={fileUrl(`/tenants/${tenantId}/attachments/${latest.id}/download`)}
             target="_blank" rel="noreferrer"
             data-testid={`ew-workbook-latest-${workshop.id}`}
             className="text-deepblue hover:underline">
            v{latest.version} · {latest.filename}
          </a>
          {files.length > 1 && (
            <button data-testid={`ew-workbook-history-${workshop.id}`}
                    onClick={() => setShowAll((v) => !v)}
                    className="mt-1 block text-[9px] uppercase tracking-[0.12em] text-muted-foreground hover:text-navy">
              {showAll ? "Hide" : `History (${files.length})`}
            </button>
          )}
          {showAll && (
            <ul className="mt-1 space-y-1">
              {files.map((f) => (
                <li key={f.id}>
                  <a href={fileUrl(`/tenants/${tenantId}/attachments/${f.id}/download`)}
                     target="_blank" rel="noreferrer" className="text-[10px] text-muted-foreground hover:text-navy">
                    v{f.version} · {(f.uploaded_at || "").slice(0, 10)} · {f.uploaded_by_email}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <span className="text-muted-foreground">No workbook</span>
      )}
      <label className="mt-1 block cursor-pointer text-[9px] uppercase tracking-[0.12em] text-deepblue hover:underline">
        <input type="file" className="hidden" data-testid={`ew-workbook-input-${workshop.id}`}
               onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        Upload new version
      </label>
    </div>
  );
};

export default function ElementWorkspace() {
  const { framework, code } = useParams();
  const { tenantId, can } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("workshops");
  const [month, setMonth] = useState(MONTHS[5]);
  const [wsOpen, setWsOpen] = useState(false);
  const [ws, setWs] = useState({ name: "", link: "", workshop_date: "", remarks: "" });
  const [review, setReview] = useState({ period_label: "", notes: "" });

  const base = `/tenants/${tenantId}/frameworks/${framework}/elements/${code}`;
  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await api.get(base);
      setData(res.data);
    } catch (e) {
      toast.error(errMsg(e));
      setData({ error: errMsg(e) });
    }
  }, [base, tenantId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="label-caps animate-pulse" data-testid="ew-loading">Loading workspace</p>;
  if (data.error) return <EmptyState testid="ew-error" title="Workspace unavailable" message={data.error} />;

  const el = data.element;
  const addWorkshop = async () => {
    try {
      await api.post(`${base}/workshops`, ws);
      toast.success("Workshop added");
      setWsOpen(false);
      setWs({ name: "", link: "", workshop_date: "", remarks: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setWorkshopStatus = async (w, status) => {
    await api.put(`${base}/workshops/${w.id}`, { status }).catch((e) => toast.error(errMsg(e)));
    load();
  };
  const startAssessment = async () => {
    try {
      await api.post(`${base}/assessments`, { month, status: "in_progress" });
      toast.success(`${month} assessment started`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addReview = async () => {
    try {
      await api.post(`${base}/reviews`, review);
      setReview({ period_label: "", notes: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8" data-testid={`element-workspace-${code}`}>
      <div className="panel overflow-hidden">
        <div className="brand-bar h-1.5 w-full" />
        <div className="flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div>
            <Link to={`/${framework === "panchatanthra" ? "panchatanthra" : "viveka-kartha"}`}
                  data-testid="ew-back"
                  className="label-caps inline-flex items-center gap-1.5 hover:text-navy">
              <ArrowLeft size={12} /> Back to framework
            </Link>
            <h1 className="mt-3 font-display text-3xl tracking-tight text-navy sm:text-4xl">{el.name}</h1>
            <p className="mt-2 text-sm text-foreground/75">
              {el.sub_elements.length} sub-elements ·{" "}
              {el.sub_elements.reduce((n, s) => n + s.questions, 0)} diagnostic questions
            </p>
          </div>
          <div className="flex items-center gap-5">
            <Ring value={data.score10} tone="stroke-teal" testid="ew-score-ring">
              <span className="font-mono text-lg tabular-nums text-navy">{data.score10 ?? "—"}</span>
            </Ring>
            <div>
              <p className="label-caps">Element score</p>
              <p className="font-mono text-3xl tabular-nums text-navy" data-testid="ew-score">
                {data.score10 ?? "—"}<span className="text-lg text-muted-foreground">/10</span>
              </p>
              <p className={`text-sm font-medium ${bandTone(data.score10)}`}>{band(data.score10)}</p>
              <p className="text-xs text-muted-foreground">{fmtPeriod(data.period_label) || "No published period"}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="ew-tabs">
        {TABS.map((t, i) => (
          <button
            key={t.key}
            data-testid={`ew-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.14em] transition-all
              ${tab === t.key ? "border-navy bg-navy text-white shadow-sm"
                : "border-border text-navy hover:-translate-y-0.5 hover:border-navy"}`}
          >
            <span className="font-mono opacity-70">{i + 1}</span>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "workshops" && (
        <section className="panel overflow-hidden" data-testid="ew-workshops">
          <div className="flex items-center justify-between border-b border-border px-6 py-5">
            <div>
              <h2 className="font-display text-xl text-navy">Workshop status</h2>
              <p className="text-xs text-muted-foreground">Level-5 outputs delivered for this element.</p>
            </div>
            {can("create") && (
              <Dialog open={wsOpen} onOpenChange={setWsOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="ew-add-workshop" className="h-9 rounded-full text-[10px] uppercase tracking-[0.16em]">
                    <Plus size={13} className="mr-1.5" /> Add workshop
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-display text-2xl">New workshop</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="label-caps">Workshop name</Label>
                      <Input data-testid="ew-workshop-name" value={ws.name}
                             onChange={(e) => setWs({ ...ws, name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-caps">Workbook link (optional)</Label>
                      <Input data-testid="ew-workshop-link" value={ws.link}
                             onChange={(e) => setWs({ ...ws, link: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-caps">Workshop date</Label>
                      <Input type="date" data-testid="ew-workshop-date" value={ws.workshop_date}
                             onChange={(e) => setWs({ ...ws, workshop_date: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-caps">Remarks</Label>
                      <Textarea data-testid="ew-workshop-remarks" value={ws.remarks}
                                onChange={(e) => setWs({ ...ws, remarks: e.target.value })} />
                    </div>
                    <Button data-testid="ew-workshop-submit" className="w-full" disabled={!ws.name}
                            onClick={addWorkshop}>Add workshop</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="hidden grid-cols-[3rem_1.6fr_1.2fr_1fr_0.9fr_1.2fr] gap-3 border-b border-border px-6 py-3 text-[9px] uppercase tracking-[0.12em] text-muted-foreground md:grid">
            <span>Sl</span><span>Workshop</span><span>Workbook</span><span>Status</span><span>Date</span><span>Remarks</span>
          </div>
          <div className="divide-y divide-border">
            {data.workshops.map((w, i) => (
              <div key={w.id} data-testid={`ew-workshop-row-${w.id}`}
                   className="grid grid-cols-1 gap-2 px-6 py-4 transition-colors hover:bg-muted/50 md:grid-cols-[3rem_1.6fr_1.2fr_1fr_0.9fr_1.2fr] md:items-center md:gap-3">
                <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>
                <span className="text-sm font-medium text-navy">{w.name}</span>
                <WorkbookCell workshop={w} base={base} framework={framework} />
                <Select value={w.status} onValueChange={(v) => setWorkshopStatus(w, v)}>
                  <SelectTrigger data-testid={`ew-workshop-status-${w.id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["not_started", "in_progress", "completed"].map((s) => (
                      <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="font-mono text-xs text-muted-foreground">{w.workshop_date || "—"}</span>
                <span className="text-xs text-foreground/70">{w.remarks || "—"}</span>
              </div>
            ))}
            {data.workshops.length === 0 && (
              <EmptyState testid="ew-workshops-empty" title="No workshops yet"
                          message="Add the Level-5 workshops delivered for this element." />
            )}
          </div>
        </section>
      )}

      {tab === "assessment" && (
        <section className="panel p-6 md:p-8" data-testid="ew-assessment">
          <h2 className="font-display text-xl text-navy">Start assessment</h2>
          <p className="mt-1 text-xs text-muted-foreground">Open the monthly survey for this element.</p>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-[1fr_1fr_auto] md:items-end">
            <div className="space-y-2">
              <Label className="label-caps">Survey month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger data-testid="ew-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Status</Label>
              <div data-testid="ew-assessment-status"
                   className="flex h-10 items-center rounded-md border border-border bg-muted/40 px-3 text-sm text-navy">
                {data.assessments.find((a) => a.month === month)?.status?.replace("_", " ") || "not started"}
              </div>
            </div>
            <Button data-testid="ew-start-assessment" onClick={startAssessment}
                    className="h-10 rounded-full text-[10px] uppercase tracking-[0.16em]">
              Start assessment
            </Button>
          </div>
          <div className="mt-8 divide-y divide-border border-t border-border" data-testid="ew-assessment-history">
            {data.assessments.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-3">
                <span className="text-sm text-navy">{monthLabel(a.month)}</span>
                <StateBadge state={a.status} />
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "monthly" && (
        <section className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]" data-testid="ew-monthly">
          <div className="panel overflow-hidden">
            <div className="border-b border-border px-6 py-5">
              <h2 className="font-display text-xl text-navy">Month wise summary</h2>
              <p className="text-xs text-muted-foreground">{fmtPeriod(data.period_label) || "Latest published period"}</p>
            </div>
            <div className="divide-y divide-border">
              <div className="grid grid-cols-[1.6fr_0.5fr_1.2fr] gap-3 bg-navy px-6 py-3 text-[9px] uppercase tracking-[0.12em] text-white">
                <span>Metric</span><span>Value</span><span>Interpretation</span>
              </div>
              <div className="grid grid-cols-[1.6fr_0.5fr_1.2fr] gap-3 px-6 py-4" data-testid="ew-metric-overall">
                <span className="text-sm text-navy">Element score /10</span>
                <span className="font-mono text-sm tabular-nums">{data.score10 ?? "—"}</span>
                <span className={`text-xs ${bandTone(data.score10)}`}>{band(data.score10)}</span>
              </div>
              {data.sub_scores10.map((s) => (
                <div key={s.code} className="grid grid-cols-[1.6fr_0.5fr_1.2fr] gap-3 px-6 py-4"
                     data-testid={`ew-metric-${s.code}`}>
                  <span className="text-sm text-foreground/80">{s.name} /10</span>
                  <span className="font-mono text-sm tabular-nums">{s.score10 ?? "—"}</span>
                  <span className={`text-xs ${bandTone(s.score10)}`}>{band(s.score10)}</span>
                </div>
              ))}
              {data.sub_scores10.length === 0 && (
                <div className="px-6 py-8">
                  <p className="text-sm text-muted-foreground">
                    No published scores yet — complete the guided survey and submit it to populate this table.
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="panel p-6" data-testid="ew-monthly-dial">
            <p className="label-caps">Element score</p>
            <div className="mt-6 flex flex-col items-center gap-4">
              <Ring value={data.score10} tone="stroke-gold" testid="ew-monthly-ring">
                <span className="font-mono text-lg tabular-nums text-navy">{data.score10 ?? "—"}</span>
              </Ring>
              <p className={`text-sm font-medium ${bandTone(data.score10)}`}>{band(data.score10)}</p>
            </div>
            <div className="mt-8 space-y-4 border-t border-border pt-6">
              {el.sub_elements.map((s) => {
                const score = data.sub_scores10.find((x) => x.code === s.code)?.score10;
                return (
                  <div key={s.code}>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-foreground/80">{s.name}</span>
                      <span className="font-mono text-xs tabular-nums text-navy">{score ?? "—"}</span>
                    </div>
                    <ScoreBar value={score} className="bg-gold" />
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {tab === "overall" && (
        <section className="panel p-6 md:p-8" data-testid="ew-overall">
          <h2 className="font-display text-xl text-navy">Overall score trend</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {el.name} score across published periods, so progress is visible at a glance.
          </p>
          {data.trend.length === 0 ? (
            <EmptyState testid="ew-trend-empty" title="No trend yet"
                        message="Publish at least one score snapshot to plot the trend." />
          ) : (
            <div className="mt-8 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.trend}>
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="period" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis domain={[0, 10]} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Line type="monotone" dataKey="score10" stroke="hsl(20 86% 47%)"
                        strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      )}

      {tab === "commitments" && (
        <section className="panel overflow-hidden" data-testid="ew-commitments">
          <div className="border-b border-border px-6 py-5">
            <h2 className="font-display text-xl text-navy">Commitments</h2>
            <p className="text-xs text-muted-foreground">Actions owned against this element.</p>
          </div>
          <div className="divide-y divide-border">
            {data.commitments.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4"
                   data-testid={`ew-commitment-${c.id}`}>
                <div>
                  <p className="text-sm text-navy">{c.title}</p>
                  <p className="label-caps mt-1">Due {c.due_date || "—"}</p>
                </div>
                <StateBadge state={c.status} />
              </div>
            ))}
            {data.commitments.length === 0 && (
              <EmptyState testid="ew-commitments-empty" title="No commitments"
                          message="Commitments raised from reviews on this element will appear here."
                          action={<Button asChild variant="outline" className="rounded-full text-[10px] uppercase tracking-[0.16em]">
                            <Link to="/panchatanthra/commitments" data-testid="ew-goto-commitments">Open commitment register</Link>
                          </Button>} />
            )}
          </div>
        </section>
      )}

      {tab === "reviews" && (
        <section className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.3fr]" data-testid="ew-reviews">
          <div className="panel p-6">
            <h2 className="font-display text-xl text-navy">Log a review</h2>
            <div className="mt-5 space-y-4">
              <div className="space-y-2">
                <Label className="label-caps">Period</Label>
                <Input data-testid="ew-review-period" placeholder="Week 24 · June 2026"
                       value={review.period_label}
                       onChange={(e) => setReview({ ...review, period_label: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Observations</Label>
                <Textarea data-testid="ew-review-notes" value={review.notes}
                          onChange={(e) => setReview({ ...review, notes: e.target.value })} />
              </div>
              <Button data-testid="ew-review-submit" disabled={!review.period_label} onClick={addReview}
                      className="h-10 w-full rounded-full text-[10px] uppercase tracking-[0.16em]">
                Save review
              </Button>
            </div>
          </div>
          <div className="panel divide-y divide-border" data-testid="ew-reviews-list">
            {data.reviews.map((r) => (
              <div key={r.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-navy">{r.period_label}</p>
                  <StateBadge state={r.status} />
                </div>
                <p className="mt-1 text-xs text-foreground/70">{r.notes || "—"}</p>
              </div>
            ))}
            {data.reviews.length === 0 && (
              <EmptyState testid="ew-reviews-empty" title="No reviews logged"
                          message="Weekly review notes for this element will show up here." />
            )}
          </div>
        </section>
      )}
    </div>
  );
}
